import {
  Badge, Button, Card, DataTable, LARGEST_PAGE, PageHeader, Row, Stack, complaintTone, isOverdue, severityTone,
  useApi, useI18n,
  type ComplaintResponse,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

type Tab = 'unassigned' | 'mine' | 'open' | 'all';

/**
 * The complaint queue (§4.14).
 *
 * The list an administrator works from, so it is sorted and filtered for that rather than for browsing:
 * unassigned first, because a complaint nobody owns is the one that goes unanswered, and an overdue marker
 * against the setting's own acknowledgement target rather than against anything the platform imposes.
 *
 * Nothing on this screen has to hide a complaint from the person it names — the API never sends one. A
 * manager who is the subject of three open complaints sees a queue of three fewer and is told nothing.
 */
export function Complaints() {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const [tab, setTab] = useState<Tab>('open');

  const query = useQuery({
    queryKey: ['complaints', tab],
    queryFn: () => api.complaints.list({
      assignedToMe: tab === 'mine' ? true : undefined,
      openOnly: tab === 'open' || tab === 'unassigned' ? true : undefined,
      pageSize: LARGEST_PAGE,
    }),
  });

  const rows = (query.data?.items ?? [])
    .filter((complaint) => tab !== 'unassigned' || complaint.assignedToMembershipId == null);

  const tabs: Tab[] = ['unassigned', 'mine', 'open', 'all'];

  return (
    <Stack>
      <PageHeader title={t('complaints.title')} lede={t('complaints.lede')} />

      {/* Existing primitives rather than a new chip class: no component in this product branches on the
          theme, and a bespoke control here would be one more place for dark mode to be got wrong. */}
      <Row>
        {tabs.map((option) => (
          <Button
            key={option}
            type="button"
            variant={option === tab ? 'primary' : 'ghost'}
            aria-pressed={option === tab}
            onClick={() => setTab(option)}
          >
            {t(`complaints.tab.${option}`)}
          </Button>
        ))}
      </Row>

      <Card flush>
        <DataTable
          caption={t('complaints.title')}
          rows={rows}
          rowKey={(complaint: ComplaintResponse) => complaint.id}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          empty={t('complaints.empty')}
          columns={[
            {
              key: 'reference',
              header: t('complaint.reference'),
              render: (complaint: ComplaintResponse) => (
                <span className="dg-muted">{complaint.reference}</span>
              ),
            },
            {
              // A real link rather than a clickable row: a `<tr>` cannot be reached by keyboard or
              // announced as actionable, and the control carries the complaint's own subject as its name.
              key: 'subject',
              header: t('complaints.subject'),
              render: (complaint: ComplaintResponse) => (
                <Stack>
                  <Link className="dg-row-button" to={`${complaint.id}`}>{complaint.subject}</Link>
                  <span className="dg-muted">
                    {t(`complaint.category.${complaint.category}`)}
                    {complaint.childName != null ? ` · ${complaint.childName}` : ''}
                  </span>
                </Stack>
              ),
            },
            {
              key: 'status',
              header: t('complaint.status'),
              render: (complaint: ComplaintResponse) => (
                <Row>
                  <Badge tone={complaintTone(complaint.status)}>
                    {t(`complaint.status.${complaint.status}`)}
                  </Badge>
                  {/* Against the tenant's own target, and a cue only — nothing escalates on it. */}
                  {isOverdue(complaint) && <Badge tone="danger">{t('complaints.overdue')}</Badge>}
                </Row>
              ),
            },
            {
              key: 'severity',
              header: t('complaint.severity'),
              render: (complaint: ComplaintResponse) => (
                <Badge tone={severityTone(complaint.severity)}>
                  {t(`complaint.severity.${complaint.severity}`)}
                </Badge>
              ),
            },
            {
              key: 'assigned',
              header: t('complaints.assignedTo'),
              render: (complaint: ComplaintResponse) =>
                complaint.assignedToName ?? <span className="dg-muted">{t('complaints.unassigned')}</span>,
            },
            {
              key: 'raised',
              header: t('complaints.raisedOn'),
              render: (complaint: ComplaintResponse) => formatDate(complaint.createdAt),
            },
          ]}
        />
      </Card>
    </Stack>
  );
}
